import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect, useState } from 'react';
import { Comments as _Comments, OnCreateCommentProps, OnDeleteCommentProps, OnToggleReactionProps, OnUpdateCommentProps } from '@/components/element/Comments';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { usePaymentTaskComments, usePaymentTaskCommentsInvalidate } from '@/hooks/api/1110_payment_task_comments';
import { useCreatePaymentTaskComment } from '@/hooks/api/1120_create_payment_task_comment';
import { useParams } from 'react-router-dom';
import { usePurchaseOrderMembers } from '@/hooks/api/0590_purchase_order_members';
import { usePaymentTaskCommentReaction } from '@/hooks/api/1121_payment_task_comment_reactions';
import { MentionData } from '@draft-js-plugins/mention';
import { usePurchaseOrderTargetUsers } from '@/hooks/api/0541_purchase_order_target_users';
import { useUpdatePaymentTaskComment } from '@/hooks/api/1122_update_payment_task_comment';
import { useDeletePaymentTaskComment } from '@/hooks/api/1123_delete_payment_task_comment';

export const Comments = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const [mentions, setMentions] = useState<MentionData[]>([]);
  const { data: comments } = usePaymentTaskComments({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const { mutate, data, isPending, error } = useCreatePaymentTaskComment();
  const { mutate: updateMutate } = useUpdatePaymentTaskComment();
  const { mutate: deleteMutate } = useDeletePaymentTaskComment();
  const { invalidate: invalidateComments } = usePaymentTaskCommentsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const { mutate: reactionMutate } = usePaymentTaskCommentReaction();
  const { data: members } = usePurchaseOrderTargetUsers({ identifier: _w_!.identifier, team_id: _t_!.id, id: Number(purchase_order_id) });

  useEffect(() => {
    if (members) {
      setMentions(() =>
        members.map((d) => ({
          id: d.id,
          name: d.user_account.name,
          avatar: d.user_account.icon_file_url,
        }))
      );
    }
  }, [members]);

  const onCreateComment = ({ content, content_json, setComment, setError, parent_id }: OnCreateCommentProps) => {
    mutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: Number(purchase_order_id),
        task_id: task!.id,
        content: content!,
        content_json: content_json,
        parent_id: parent_id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onUpdateComment = ({ id, content, content_json, setComment, setError }: OnUpdateCommentProps) => {
    updateMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: Number(purchase_order_id),
        task_id: task!.id,
        id: id,
        content: content!,
        content_json: content_json,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onDeleteComment = ({ id, setComment, setError }: OnDeleteCommentProps) => {
    deleteMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: Number(purchase_order_id),
        task_id: task!.id,
        id: id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
          invalidateComments();
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onToggleReaction = ({ comment_id, emoji_id, content, setComment, setError }: OnToggleReactionProps) => {
    reactionMutate(
      { identifier: _w_!.identifier, team_id: _t_!.id, purchase_order_id: Number(purchase_order_id), task_id: task!.id, id: comment_id, emoji_id, content },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => setError(error),
        onSettled: () => {},
      }
    );
  };

  if (mentions.length == 0) return <></>;

  return (
    <_Comments
      identifier={`payment_${_w_!.identifier}_${_t_!.id}_${purchase_order_id}_${task!.id}`}
      comments={comments}
      user={me!}
      mentions={mentions}
      onCreateComment={onCreateComment}
      onToggleReaction={onToggleReaction}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
    />
  );
};
